import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import html2canvas from 'html2canvas';
import DayMap from './DayMap';
import { safeFormatDate } from '../shared/utils';
import { activityMatchesOption, isDayWithinAccommodationRange, findMultiDayAccommodations } from '../shared/activityUtils';

const SHORTS_WIDTH = 1080;
const SHORTS_HEIGHT = 1920;
const PREVIEW_MAX_WIDTH = 320;

function getEnglishName(activity) {
  return activity.nameInEnglish?.trim() || activity.name?.trim() || '';
}

function getEnglishCity(location) {
  if (!location || typeof location !== 'object') return '';
  return location.cityInEnglish?.trim() || location.city?.trim() || '';
}

function getDayDatePart(dateTime) {
  if (!dateTime || typeof dateTime !== 'string') return '';
  return dateTime.split('T')[0] || '';
}

function getAccommodationDisplayName(accommodation, showAccommodationName) {
  if (showAccommodationName) {
    return getEnglishName(accommodation);
  }
  const city = getEnglishCity(accommodation.location);
  return city || getEnglishName(accommodation);
}

function resolveDayAccommodations(parsedData, day, selectedOption) {
  if (!parsedData || !day) return [];

  const dayDate = (day.date || '').split('T')[0];
  const multiDayAccoms = findMultiDayAccommodations(parsedData);
  const continueAccoms = multiDayAccoms.filter(
    (accom) =>
      isDayWithinAccommodationRange(day.date, accom.startDateTime, accom.endDateTime) &&
      accom.originalDay !== day.date &&
      activityMatchesOption(accom, selectedOption)
  );
  const nightlyAccoms = (day.activities || []).filter(
    (activity) =>
      activity.type === 'accommodation' &&
      activityMatchesOption(activity, selectedOption) &&
      getDayDatePart(activity.startDateTime) === dayDate
  );

  const seen = new Set();
  const merged = [...continueAccoms, ...nightlyAccoms].filter((accom) => {
    const key = accom.id || `${getEnglishName(accom)}-${accom.startDateTime || dayDate}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return merged;
}

function buildAccommodationMapLocation(accommodation, day, dayIndex, showAccommodationName) {
  const location = accommodation.location;
  const hasCoords = Boolean(
    location && typeof location === 'object' && location.latitude && location.longitude
  );
  if (!hasCoords) return null;

  const displayName = getAccommodationDisplayName(accommodation, showAccommodationName);
  const address =
    location && typeof location === 'object' ? location.address || '' : location || '';

  return {
    name: displayName,
    type: 'accommodation',
    day: dayIndex + 1,
    date: day.date ? safeFormatDate(day.date) : '',
    address,
    time: null,
    isOngoing: true,
    isAccommodation: true,
    gps: {
      latitude: Number(location.latitude),
      longitude: Number(location.longitude),
    },
  };
}

function buildAttractionEntries(day, dayIndex, selectedOption) {
  if (!day?.activities) return [];

  return day.activities
    .filter((activity) => activity.type === 'attraction' && activityMatchesOption(activity, selectedOption))
    .map((activity) => {
      const englishName = getEnglishName(activity);
      const hasCoords = Boolean(
        activity.location &&
        typeof activity.location === 'object' &&
        activity.location.latitude &&
        activity.location.longitude
      );

      const address =
        activity.location && typeof activity.location === 'object'
          ? activity.location.address
          : activity.location || '';

      return {
        id: activity.id || `${englishName}-${activity.startDateTime || day.date}`,
        name: englishName,
        hasCoords,
        mapLocation: hasCoords
          ? {
              name: englishName,
              type: 'attraction',
              day: dayIndex + 1,
              date: day.date ? safeFormatDate(day.date) : '',
              address,
              time: activity.startDateTime ? safeFormatDate(activity.startDateTime, 'HH:mm') : null,
              isOngoing: false,
              gps: {
                latitude: Number(activity.location.latitude),
                longitude: Number(activity.location.longitude),
              },
            }
          : null,
      };
    });
}

function ShortsFrame({
  frameRef,
  planName,
  dayTitle,
  selectedCount,
  selectedNames,
  locations,
  mapKey,
  dayIndex,
  onMapReady,
  width,
  height,
  headerPadding,
  planNameSize,
  dayTitleSize,
  countSize,
  namesSize,
  markerBadgeMode,
  t,
  tripPlanLabel,
}) {
  const previewHeight = Math.round(width * (SHORTS_HEIGHT / SHORTS_WIDTH));

  return (
    <div
      ref={frameRef}
      style={{
        width,
        height: height || previewHeight,
        background: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: headerPadding,
          background: 'linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)',
          color: '#fff',
          flexShrink: 0,
        }}
      >
        <div style={{ fontSize: planNameSize, opacity: 0.9, marginBottom: '8px' }}>
          {planName || tripPlanLabel}
        </div>
        <div style={{ fontSize: dayTitleSize, fontWeight: 700, lineHeight: 1.15 }}>{dayTitle}</div>
        {selectedCount > 0 && (
          <div style={{ fontSize: countSize, marginTop: '12px', opacity: 0.95 }}>
            {t('planDetail.days.shortsMap.attractionsCount', { count: selectedCount })}
          </div>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 120, width: '100%', background: '#e9ecef' }}>
        {locations.length > 0 ? (
          <DayMap
            key={mapKey}
            locations={locations}
            height="100%"
            dayIndex={dayIndex}
            disableSavedView
            interactive={false}
            onMapReady={onMapReady}
            language="en"
            markerBadgeMode={markerBadgeMode}
          />
        ) : (
          <div
            className="d-flex align-items-center justify-content-center h-100 text-muted"
            style={{ fontSize: namesSize, padding: '24px', textAlign: 'center' }}
          >
            {t('planDetail.days.shortsMap.selectAtLeastOne')}
          </div>
        )}
      </div>
      {selectedNames.length > 0 && (
        <div
          style={{
            flexShrink: 0,
            maxHeight: Math.round((height || previewHeight) * 0.22),
            overflow: 'hidden',
            padding: headerPadding,
            background: '#fff',
          }}
        >
          <div style={{ fontSize: namesSize, fontWeight: 600, marginBottom: '8px' }}>
            {t('planDetail.days.shortsMap.attractionsList')}
          </div>
          <ol style={{ margin: 0, paddingLeft: '1.2em', fontSize: namesSize, lineHeight: 1.35 }}>
            {selectedNames.map((name) => (
              <li key={name} style={{ marginBottom: '4px' }}>{name}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export default function DayShortsMapModal({
  show,
  onHide,
  day,
  dayIndex,
  planName,
  parsedData,
  selectedOption = 1,
}) {
  const { t } = useTranslation(undefined, { lng: 'en' });
  const previewRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mapKey, setMapKey] = useState(Date.now());
  const [markerBadgeMode, setMarkerBadgeMode] = useState('hours');
  const [showAccommodationOnMap, setShowAccommodationOnMap] = useState(true);
  const [showAccommodationName, setShowAccommodationName] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [previewMapReady, setPreviewMapReady] = useState(false);

  const attractionEntries = useMemo(
    () => buildAttractionEntries(day, dayIndex, selectedOption),
    [day, dayIndex, selectedOption]
  );

  const mappableEntries = useMemo(
    () => attractionEntries.filter((entry) => entry.hasCoords),
    [attractionEntries]
  );

  const selectedEntries = useMemo(
    () => mappableEntries.filter((entry) => selectedIds.has(entry.id)),
    [mappableEntries, selectedIds]
  );

  const selectedNames = useMemo(
    () => selectedEntries.map((entry) => entry.name).filter(Boolean),
    [selectedEntries]
  );

  const dayAccommodations = useMemo(
    () => resolveDayAccommodations(parsedData, day, selectedOption),
    [parsedData, day, selectedOption]
  );

  const mappableAccommodations = useMemo(
    () =>
      dayAccommodations
        .map((accom) => buildAccommodationMapLocation(accom, day, dayIndex, showAccommodationName))
        .filter(Boolean),
    [dayAccommodations, day, dayIndex, showAccommodationName]
  );

  const mapLocations = useMemo(() => {
    const locations = [];

    if (showAccommodationOnMap) {
      locations.push(...mappableAccommodations);
    }

    let attractionNumber = 1;
    selectedEntries.forEach((entry) => {
      locations.push({
        ...entry.mapLocation,
        isAccommodation: false,
        markerNumber: attractionNumber,
      });
      attractionNumber += 1;
    });

    return locations;
  }, [showAccommodationOnMap, mappableAccommodations, selectedEntries]);

  const dayTitle = day?.date
    ? `Day ${dayIndex + 1}: ${format(new Date(day.date), 'PPP', { locale: enUS })}`
    : `Day ${dayIndex + 1}`;

  const previewHeight = Math.round(PREVIEW_MAX_WIDTH * (SHORTS_HEIGHT / SHORTS_WIDTH));
  const exportScale = SHORTS_WIDTH / PREVIEW_MAX_WIDTH;

  useEffect(() => {
    if (!show) return;
    setMapKey(Date.now());
    setExportError('');
    setPreviewMapReady(false);
    setSelectedIds(new Set(mappableEntries.map((entry) => entry.id)));
    setShowAccommodationOnMap(mappableEntries.length <= 1);
    setShowAccommodationName(true);
  }, [show, day?.id, selectedOption, mappableEntries]);

  useEffect(() => {
    setPreviewMapReady(false);
    setMapKey(Date.now());
  }, [selectedIds, markerBadgeMode, showAccommodationOnMap, showAccommodationName]);

  const handlePreviewMapReady = useCallback(() => {
    setPreviewMapReady(true);
  }, []);

  const toggleAttraction = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(mappableEntries.map((entry) => entry.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleDownload = async () => {
    if (!previewRef.current || mapLocations.length === 0) return;

    setIsExporting(true);
    setExportError('');

    try {
      if (!previewMapReady) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      // Let map tiles and marker badges finish painting
      await new Promise((resolve) => setTimeout(resolve, 800));

      const canvas = await html2canvas(previewRef.current, {
        scale: exportScale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const link = document.createElement('a');
      const safePlanName = (planName || 'trip').replace(/[^\w\-]+/g, '-').replace(/-+/g, '-');
      link.download = `${safePlanName}-day-${dayIndex + 1}-shorts-map.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Failed to export shorts map:', error);
      setExportError(t('planDetail.days.shortsMap.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  const frameProps = {
    planName,
    dayTitle,
    selectedCount: selectedEntries.length,
    selectedNames,
    locations: mapLocations,
    dayIndex,
    markerBadgeMode,
    t,
    tripPlanLabel: t('planDetail.overview.tripPlan'),
  };

  const accommodationPreviewLabel = dayAccommodations.length
    ? getAccommodationDisplayName(dayAccommodations[0], showAccommodationName)
    : '';

  const markerLabelOptions = [
    { value: 'none', label: t('planDetail.days.shortsMap.markerLabelsNone') },
    { value: 'hours', label: t('planDetail.days.shortsMap.markerLabelsHours') },
    { value: 'numbers', label: t('planDetail.days.shortsMap.markerLabelsNumbers') },
    { value: 'names', label: t('planDetail.days.shortsMap.markerLabelsNames') },
  ];

  return (
    <Modal show={show} onHide={onHide} centered size="lg" fullscreen="lg-down" onEntered={() => setMapKey(Date.now())}>
      <Modal.Header closeButton dir="ltr" lang="en">
        <Modal.Title>
          <i className="bi bi-camera-video me-2"></i>
          {t('planDetail.days.shortsMap.title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body dir="ltr" lang="en">
        {attractionEntries.length === 0 ? (
          <Alert variant="info" className="mb-0">
            {t('planDetail.days.shortsMap.noAttractions')}
          </Alert>
        ) : (
          <div className="row g-3">
            <div className="col-md-5">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">{t('planDetail.days.shortsMap.selectAttractions')}</h6>
                <div className="d-flex gap-2">
                  <Button variant="link" size="sm" className="p-0" onClick={selectAll}>
                    {t('planDetail.days.shortsMap.selectAll')}
                  </Button>
                  <Button variant="link" size="sm" className="p-0" onClick={deselectAll}>
                    {t('planDetail.days.shortsMap.deselectAll')}
                  </Button>
                </div>
              </div>
              <div className="border rounded p-2 mb-3" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                {attractionEntries.map((entry) => (
                  <Form.Check
                    key={entry.id}
                    type="checkbox"
                    id={`shorts-attraction-${entry.id}`}
                    className="mb-2"
                    label={
                      <span className={entry.hasCoords ? '' : 'text-muted'}>
                        {entry.name || t('planDetail.days.notSpecified')}
                        {!entry.hasCoords && (
                          <small className="ms-1">({t('planDetail.days.shortsMap.noCoordinates')})</small>
                        )}
                      </span>
                    }
                    checked={selectedIds.has(entry.id)}
                    disabled={!entry.hasCoords}
                    onChange={() => toggleAttraction(entry.id)}
                  />
                ))}
              </div>
              {mappableEntries.length === 0 && (
                <Alert variant="warning" className="mb-3">
                  {t('planDetail.days.shortsMap.noCoordinatesWarning')}
                </Alert>
              )}

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">{t('planDetail.days.shortsMap.markerLabels')}</Form.Label>
                {markerLabelOptions.map((option) => (
                  <Form.Check
                    key={option.value}
                    type="radio"
                    id={`shorts-marker-${option.value}`}
                    name="shorts-marker-labels"
                    label={option.label}
                    checked={markerBadgeMode === option.value}
                    onChange={() => setMarkerBadgeMode(option.value)}
                  />
                ))}
              </Form.Group>

              <Form.Group className="mb-0">
                <Form.Label className="fw-semibold">{t('planDetail.days.shortsMap.accommodation')}</Form.Label>
                <Form.Check
                  type="checkbox"
                  id="shorts-show-accommodation"
                  label={t('planDetail.days.shortsMap.showAccommodationOnMap')}
                  checked={showAccommodationOnMap}
                  disabled={mappableAccommodations.length === 0}
                  onChange={(e) => setShowAccommodationOnMap(e.target.checked)}
                />
                {mappableAccommodations.length === 0 && (
                  <Form.Text className="text-muted d-block">
                    {t('planDetail.days.shortsMap.noAccommodationCoordinates')}
                  </Form.Text>
                )}
                {showAccommodationOnMap && mappableAccommodations.length > 0 && (
                  <>
                    <Form.Check
                      type="checkbox"
                      id="shorts-show-accommodation-name"
                      className="mt-2"
                      label={t('planDetail.days.shortsMap.showAccommodationName')}
                      checked={showAccommodationName}
                      onChange={(e) => setShowAccommodationName(e.target.checked)}
                    />
                    <Form.Text className="text-muted d-block">
                      {showAccommodationName
                        ? t('planDetail.days.shortsMap.accommodationLabelName', { name: accommodationPreviewLabel })
                        : t('planDetail.days.shortsMap.accommodationLabelCity', { city: accommodationPreviewLabel })}
                    </Form.Text>
                  </>
                )}
              </Form.Group>
            </div>

            <div className="col-md-7">
              <h6 className="mb-2">{t('planDetail.days.shortsMap.preview')}</h6>
              <p className="text-muted small mb-2">{t('planDetail.days.shortsMap.aspectRatioHelp')}</p>
              <div
                className="mx-auto border rounded overflow-hidden shadow-sm"
                style={{ width: PREVIEW_MAX_WIDTH, height: previewHeight }}
              >
                <ShortsFrame
                  {...frameProps}
                  frameRef={previewRef}
                  mapKey={mapKey}
                  onMapReady={handlePreviewMapReady}
                  width={PREVIEW_MAX_WIDTH}
                  height={previewHeight}
                  headerPadding="12px 14px"
                  planNameSize="11px"
                  dayTitleSize="16px"
                  countSize="11px"
                  namesSize="10px"
                />
              </div>
              {!previewMapReady && mapLocations.length > 0 && (
                <p className="text-muted small text-center mt-2 mb-0">
                  {t('planDetail.days.shortsMap.loadingMap')}
                </p>
              )}
            </div>
          </div>
        )}

        {exportError && (
          <Alert variant="danger" className="mt-3 mb-0">
            {exportError}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer dir="ltr" lang="en">
        <Button variant="outline-secondary" onClick={onHide}>
          {t('common.close', { defaultValue: 'Close' })}
        </Button>
        <Button
          variant="primary"
          onClick={handleDownload}
          disabled={isExporting || mapLocations.length === 0 || !previewMapReady}
        >
          <i className="bi bi-download me-2"></i>
          {isExporting ? t('planDetail.days.shortsMap.downloading') : t('planDetail.days.shortsMap.download')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
